// src/pages/LaptopSelection.js
import React from 'react';
import { Link } from 'react-router-dom';
// Icons for brands (Using Si for brand recognition)
import { SiApple, SiDell, SiHp, SiLenovo, SiAsus, SiAcer } from 'react-icons/si';
// Icons for repair types and general UI (Using Fa)
import { FaWindows, FaLaptopMedical, FaKeyboard, FaPlug, FaBatteryFull, FaTv, FaDatabase, FaQuestionCircle, FaWrench, FaPhoneAlt } from 'react-icons/fa';

// --- Black & White Button Style ---
const primaryButtonStyle = {
    display: 'inline-block', alignItems: 'center', background: '#007BFF', color: '#fff',
    border: '1px solid #007BFF', borderRadius: '4px', padding: '0.75rem 1.5rem',
    cursor: 'pointer', fontSize: '1rem', textDecoration: 'none', fontWeight: '500',
    transition: 'background-color 0.2s, color 0.2s',
    textAlign: 'center',
};
// Hover effect simulation comment (CSS is better):
// &:hover { background: '#6c757d', color: '#fff' }

// Secondary link style (e.g., phone number) - Black/Grey text link
const secondaryLinkStyle = {
    color: '#007BFF',
    fontWeight: '500',
    textDecoration: 'underline',
};
// --- ---


// --- Data for Brands and Repairs ---
const commonBrands = [ /* ... same as before ... */
    { name: 'Apple', Icon: SiApple }, { name: 'Dell', Icon: SiDell },
    { name: 'HP', Icon: SiHp }, { name: 'Lenovo', Icon: SiLenovo },
    { name: 'Asus', Icon: SiAsus }, { name: 'Acer', Icon: SiAcer },
    { name: 'Windows Laptops', Icon: FaWindows },
];
const commonRepairs = [ /* ... same as before ... */
    { name: 'Screen Replacement', Icon: FaTv }, { name: 'Battery Replacement', Icon: FaBatteryFull },
    { name: 'Keyboard Repair', Icon: FaKeyboard }, { name: 'Charging Port', Icon: FaPlug },
    { name: 'Diagnostics', Icon: FaLaptopMedical }, { name: 'Data Recovery', Icon: FaDatabase },
    { name: 'Other Issues', Icon: FaQuestionCircle },
];
// --- ---

function LaptopSelection() {
  return (
    // Assuming page-container provides basic structure/padding. Retaining Bootstrap classes.
    <main className="laptop-selection-page container py-5">
      <header className="text-center mb-5">
         {/* Using black/dark grey for headings/text */}
        <h1 style={{ fontSize: '2.2rem', color: '#007BFF', marginBottom: '0.75rem' }}>Laptop Repair Services</h1>
        <p className="lead" style={{ maxWidth: '700px', margin: '0 auto', color: '#6c757d' }}> {/* Dark grey lead text */}
          Expert repairs for all major laptop brands. Due to the complexity and fluctuating part prices,
          we provide personalized quotes for accurate pricing.
        </p>
      </header>

      {/* Section showing brands serviced */}
      <section className="mb-5" aria-labelledby="brands-heading">
         <h4 id="brands-heading" className="text-center mb-4" style={sectionHeadingStyle}>Brands We Service</h4>
         <div style={brandLogosContainerStyle}>
             {commonBrands.map(brand => (
                 <div key={brand.name} style={brandLogoItemStyle} title={brand.name}>
                     {/* Brand icons set to dark grey */}
                     <brand.Icon size={40} style={{ color: '#6c757d' }}/>
                 </div>
             ))}
         </div>
      </section>

       {/* Section showing common repairs */}
       <section className="mb-5" aria-labelledby="repairs-heading">
         <h4 id="repairs-heading" className="text-center mb-4" style={sectionHeadingStyle}>Common Laptop Repairs</h4>
         <div className="repairs-grid" style={repairsGridStyle}>
            {commonRepairs.map(repair => (
                <div key={repair.name} style={repairItemStyle}>
                    {/* Repair icons set to dark grey/black */}
                    <repair.Icon size={30} style={{ marginBottom: '0.75rem', color: '#6c757d' }}/>
                    <span style={{ fontSize: '0.95rem', fontWeight: '500', color: '#6c757d' }}>{repair.name}</span>
                </div>
            ))}
         </div>
       </section>

      {/* Call to Action Section */}
      <section className="text-center" style={ctaSectionStyle}>
         {/* Wrench icon set to grey */}
         <FaWrench size={24} style={{marginBottom: '1rem', color: '#6c757d'}}/>
        <h4 style={{marginBottom: '0.5rem', color: '#007BFF'}}>Ready for a Fix?</h4>
        <p className="mb-4" style={{color: '#6c757d', lineHeight: '1.6'}}> {/* Dark grey text */}
          Repair times for laptops may vary depending on the type and complexity of the issue. Contact us for a free quote and speak directly with one of our expert laptop technicians to find the best solution for your device.
        </p>
        <Link to="/contact" style={primaryButtonStyle} className="cta-button">
          Request Your Quote Now
        </Link>
        <p style={{marginTop: '1.5rem', fontSize: '0.9rem', color: '#6c757d'}}> {/* Grey text */}
            Or call us: <a href="tel:07944859580" style={secondaryLinkStyle}>07944 859580</a>
        </p>
      </section>

    </main>
  );
}

// --- Black & White Inline Styles ---
const sectionHeadingStyle = {
    fontSize: '1.5rem',
    color: '#007BFF', // Blue heading
    fontWeight: '500',
};

const brandLogosContainerStyle = {
    display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center',
    gap: '2rem',
};

const brandLogoItemStyle = {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    opacity: 0.85, // Slightly less muted
};

const repairsGridStyle = {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1.5rem', maxWidth: '800px', margin: '0 auto',
};

const repairItemStyle = {
    display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
    padding: '1.5rem 1rem',
    backgroundColor: '#fff', // White background
    border: '1px solid #dee2e6', // Light grey border
    borderRadius: '8px',
    // Use a subtle shadow with less color influence
    boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
};

const ctaSectionStyle = {
    marginTop: '3rem', padding: '2.5rem',
    backgroundColor: '#f8f9fa', // Very light grey background
    borderRadius: '8px',
    border: '1px solid #dee2e6', // Light grey border
};
// --- ---

export default LaptopSelection;